import CheckIcon from '@mui/icons-material/Check';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { achievementsActions } from 'app-components/flux/achievements';
import { freeToSpendTypes } from 'app-components/flux/free-to-spend';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';
import intoWallet from 'assets/illustrations/Money-In.svg';
import outOfWallet from 'assets/illustrations/Money-out.svg';
import spendingPlan120 from 'assets/illustrations/Spending-Plan.png';
import bubble from 'assets/images/spendingPlan/bubble.svg';
import spendingPlanKeyDark from 'assets/images/spendingPlan/spending-plan-key-dark.svg';
import spendingPlanKeyLight from 'assets/images/spendingPlan/spending-plan-key-light.svg';
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import useQPreferences from 'components/QPreferences/useQPreferences';
import SkeletonRow from 'components/Upcoming/skeleton';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  newSavingsRoot: {
    height: 489,
    textAlign: 'center',
    alignContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  iconRow: {
    marginRight: 'auto',
    marginLeft: 'auto',
    alignContent: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  topText: {
    fontSize: '18px',
    marginTop: 20,
    marginBottom: 20,
    fontFamily: 'Rubik',
  },
  circleTextContainer: {
    textAlign: 'center',
    alignContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 25,
    paddingLeft: 25,
  },
  thinLine: {
    width: '80%',
    marginRight: 'auto',
    marginLeft: 'auto',
    marginTop: -30,
    marginBottom: 20,
  },
  spendingPlanText: {
    marginTop: '52px',
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  checkList: {
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '20px',
    width: '50%',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
  },
  checkItem: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '35px',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  checkItemText: {
    fontSize: '16px',
    color: theme.palette.text.secondary,
    '&.checked': {
      color: theme.palette.text.primary,
      marginLeft: '-26px',
    },
  },
  checkMark: {
    marginLeft: -30,
    color: theme.palette.text.primary,
  },
  circleIconContainer: {
    marginRight: 'auto',
    marginLeft: 'auto',
    textAlign: 'center',
    alignContent: 'center',
    width: '98px',
    height: '98px',
    borderRadius: 50,
    color: theme.palette.info.light,
    backgroundColor: theme.palette.info.light,
  },
  pieChartContainer: {
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  bubbleText: {
    fontSize: '16px',
    fontWeight: 500,
    marginTop: '15px',
  },
  letsGoContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginTop: 'auto',
    padding: '0 24px 24px 24px',
  },
}));

const { GSFStageEnum } = freeToSpendTypes;

function SpendingPlanFirstUse() {
  const [open, setOpen] = useState(true);
  const [screenIndex, setScreenIndex] = useState(0);
  const [numberChecked, setNumberChecked] = useState(0);
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const preferences = useQPreferences();
  const [_gettingStartedFlowPhase, setGettingStartedFlowPhase] = usePreferenceV2(
    'sSPGettingStartedFlowPhase',
    GSFStageEnum.COMPLETE,
  );

  const onClose = useCallback(() => {
    dispatch(rootUiActions.removeDialog());
    setOpen(false);
  }, [dispatch, setOpen]);

  const submit = useCallback(() => {
    navigate('/spending-plan');
    setGettingStartedFlowPhase(GSFStageEnum.COMPLETE);
    preferences.setDatasetPreference({ spendingPlanSetupCompleted: true });
    dispatch(achievementsActions.sendAchievementsEvent({ source: 'SPENDING_PLAN', eventType: 'CREATE' }));
    tracker.track(tracker.events.checklistItemComplete, { section: 'spending plan' });
    tracker.track(tracker.events.spendingPlanSetupCompleted);
    onClose();
  }, [navigate, onClose, preferences, dispatch, setGettingStartedFlowPhase]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (screenIndex === 1) {
        setNumberChecked(numberChecked + 1);
      }
      if (numberChecked >= 4) {
        submit();
      }
    }, 1000);
    // clearing interval
    return () => clearInterval(timer);
  });

  const newScreens = () => {
    const retArr: any[] = [];
    retArr.push(
      <div className={classes.newSavingsRoot}>
        <Typography className={classes.topText}>What we’ll use to automatically build your monthly plan</Typography>
        <div className={classes.iconRow}>
          <div className={classes.circleTextContainer}>
            <div className={classes.circleIconContainer}>
              <img
                alt='Income'
                src={intoWallet}
                style={{
                  height: '80px',
                  marginTop: 10,
                }}
              />
            </div>
            <Typography className={classes.bubbleText}>Income</Typography>
          </div>
          <div className={classes.circleTextContainer}>
            <div className={classes.circleIconContainer}>
              <img
                alt='Bills & Subscriptions'
                src={outOfWallet}
                style={{
                  height: '80px',
                  marginTop: 15,
                }}
              />
            </div>
            <Typography className={classes.bubbleText}>Bills & Subscriptions</Typography>
          </div>
          <div className={classes.circleTextContainer}>
            <div className={classes.circleIconContainer}>
              <img
                alt='Other Spending'
                src={bubble}
                style={{
                  height: '60px',
                  marginTop: 18,
                }}
              />
            </div>
            <Typography className={classes.bubbleText}>Other Spending</Typography>
          </div>
        </div>
        <div className={classes.thinLine}>
          <SkeletonRow />
        </div>
        <Typography className={classes.topText}>
          {"Then, we'll keep you updated on what's available as you spend"}
        </Typography>
        <div className={classes.pieChartContainer}>
          <img
            alt='Spending Plan Pie Chart'
            src={theme.palette.mode === 'light' ? spendingPlanKeyLight : spendingPlanKeyDark}
          />
        </div>
        <div className={classes.letsGoContainer}>
          <QButton id={'lets-go-btn'} onClick={() => setScreenIndex(1)} variant={'contained'}>
            {"Let's Go"}
          </QButton>
        </div>
      </div>,
    );
    retArr.push(
      <div className={classes.newSavingsRoot}>
        <img
          alt='Spending Plan Icon'
          src={spendingPlan120}
          style={{
            margin: '0 auto',
            padding: '24px',
            height: '168px',
            width: '168px',
          }}
        />
        <Typography variant='headlineMedium' className={classes.spendingPlanText}>
          Configuring your Spending Plan
        </Typography>
        <div className={classes.checkList}>
          <div className={classes.checkItem}>
            {numberChecked > 0 && (
              <ListItemIcon className={classes.checkMark}>
                <CheckIcon />
              </ListItemIcon>
            )}
            <Typography className={cx(classes.checkItemText, numberChecked > 0 ? 'checked' : '')}>
              Finding your income
            </Typography>
          </div>
          <div className={classes.checkItem}>
            {numberChecked > 1 && (
              <ListItemIcon className={classes.checkMark}>
                <CheckIcon />
              </ListItemIcon>
            )}
            <Typography className={cx(classes.checkItemText, numberChecked > 1 ? 'checked' : '')}>
              Finding your bills & subscriptions
            </Typography>
          </div>
          <div className={classes.checkItem}>
            {numberChecked > 2 && (
              <ListItemIcon className={classes.checkMark}>
                <CheckIcon />
              </ListItemIcon>
            )}
            <Typography className={cx(classes.checkItemText, numberChecked > 2 ? 'checked' : '')}>
              Gathering other spending for the month
            </Typography>
          </div>
        </div>
      </div>,
    );
    return retArr;
  };

  return (
    <StdDialog
      onBack={() => setScreenIndex(screenIndex - 1)}
      onClose={onClose}
      open={open}
      sharedcomponentid={'FIRST_TIME_USE_DIALOG'}
      showCloseButton={screenIndex === 0}
      size='md'
      title={screenIndex === 0 ? 'Spending Plan overview' : ''}
    >
      {newScreens()[screenIndex]}
    </StdDialog>
  );
}

export default SpendingPlanFirstUse;
